import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../services/siteSettingsService';

const toolIcons = [
  { name: 'Make', icon: 'https://images.ctfassets.net/un655fb9wln6/11sTQC2LhhAtJyb7ptQvEl/6c2dbec03b3a2830a24e09e913ad46cc/Make-app-icon.png', color: '#000000' },
  { name: 'Zapier', icon: 'https://cdn.worldvectorlogo.com/logos/zapier-1.svg', color: '#FF4A00' },
  { name: 'AWS', icon: 'https://cdn.worldvectorlogo.com/logos/aws-2.svg', color: '#FF9900' },
  { name: 'Tableau', icon: 'https://cdn.worldvectorlogo.com/logos/tableau-software.svg', color: '#E97627' },
  { name: 'Binance', icon: 'https://cdn.worldvectorlogo.com/logos/binance-logo.svg', color: '#F3BA2F' },
  { name: 'Slack', icon: 'https://cdn.worldvectorlogo.com/logos/slack-new-logo.svg', color: '#4A154B' },
  { name: 'Stripe', icon: 'https://cdn.worldvectorlogo.com/logos/stripe-4.svg', color: '#008CDD' },
  { name: 'Notion', icon: 'https://upload.wikimedia.org/wikipedia/commons/e/e9/Notion-logo.svg', color: '#000000' },
  { name: 'GitHub', icon: 'https://cdn.worldvectorlogo.com/logos/github-icon-1.svg', color: '#181717' },
  { name: 'Docker', icon: 'https://cdn.worldvectorlogo.com/logos/docker.svg', color: '#2496ED' },
  { name: 'MongoDB', icon: 'https://cdn.worldvectorlogo.com/logos/mongodb-icon-1.svg', color: '#47A248' },
  { name: 'Redis', icon: 'https://cdn.worldvectorlogo.com/logos/redis.svg', color: '#DC382D' },
  { name: 'PostgreSQL', icon: 'https://cdn.worldvectorlogo.com/logos/postgresql.svg', color: '#336791' },
  { name: 'React', icon: 'https://cdn.worldvectorlogo.com/logos/react-2.svg', color: '#61DAFB' },
  { name: 'Vue', icon: 'https://cdn.worldvectorlogo.com/logos/vue-9.svg', color: '#4FC08D' },
  { name: 'Angular', icon: 'https://cdn.worldvectorlogo.com/logos/angular-icon-1.svg', color: '#DD0031' }
];

const Hero = () => {
  const { settings } = useSiteSettings();
  const primaryCtaIsExternal = /^(https?:\/\/|mailto:|tel:)/i.test(settings.primaryCtaPath);

  return (
    <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-neon-orange/5 pointer-events-none" />

      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="space-y-6 text-center md:text-left"
        >
          <motion.h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="bg-gradient-to-r from-gray-900 via-neon-orange to-gray-900 bg-clip-text text-transparent">
              Libérez Votre Puissance Digitale
            </span>
          </motion.h1>

          <motion.p
            className="text-xl sm:text-2xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Faites passer votre projet au niveau supérieur grâce à la technologie, au design et à la stratégie digitale
          </motion.p>

          <motion.p
            className="text-base sm:text-lg text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {settings.brandFullName} est votre équipe de développeurs, stratèges et concepteurs
            passionnés avec la vision de transformer votre entreprise avec des solutions numériques
            réussies !
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Link
              to="/services"
              className="cta-button glow group relative w-full sm:w-auto"
            >
              <span className="relative z-10">Voir nos services</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-neon-orange to-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3 }}
              />
            </Link>

            {primaryCtaIsExternal ? (
              <a
                href={settings.primaryCtaPath}
                className="flex items-center justify-center gap-2 text-gray-700 hover:text-neon-orange transition-all duration-300 group w-full sm:w-auto"
              >
                <span className="text-neon-orange transform group-hover:scale-110 transition-transform duration-300">
                  ▶
                </span>
                {settings.primaryCtaLabel}
              </a>
            ) : (
              <Link
                to={settings.primaryCtaPath}
                className="flex items-center justify-center gap-2 text-gray-700 hover:text-neon-orange transition-all duration-300 group w-full sm:w-auto"
              >
                <span className="text-neon-orange transform group-hover:scale-110 transition-transform duration-300">
                  ▶
                </span>
                {settings.primaryCtaLabel}
              </Link>
            )}
          </motion.div>
        </motion.div>

        {/* Right Content - Swiss Army Knife */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative flex items-center justify-center"
        >
          {/* Central Swiss Army Knife */}
          <motion.div
            className="relative w-[600px] h-[600px]"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            {/* Glowing background */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-br from-neon-orange/20 to-transparent rounded-full blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Swiss Army Knife Image */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <img
                src="/swiss-army-knife.svg"
                alt="Couteau Suisse Digital"
                className="w-96 h-96 object-contain drop-shadow-2xl"
              />
            </motion.div>

            {/* Multiple Orbiting Circles */}
            {[1, 2].map((orbit, orbitIndex) => (
              <motion.div
                key={`orbit-${orbit}`}
                className="absolute inset-0"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 20 + orbitIndex * 5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                {toolIcons.slice(orbitIndex * 8, (orbitIndex + 1) * 8).map((tool, index) => {
                  const angle = (index / 8) * Math.PI * 2;
                  const radius = 200 + orbitIndex * 80;

                  return (
                    <motion.div
                      key={tool.name}
                      className="absolute"
                      style={{
                        left: `calc(50% + ${Math.cos(angle) * radius}px)`,
                        top: `calc(50% + ${Math.sin(angle) * radius}px)`,
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      <motion.div
                        className="w-12 h-12 bg-white rounded-xl shadow-lg p-2 hover:shadow-xl transition-shadow duration-300"
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.3 }}
                      >
                        <img
                          src={tool.icon}
                          alt={tool.name}
                          className="w-full h-full object-contain"
                        />
                      </motion.div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
